// /ingredients — Admin Ingredient Stock Management
//
// Lists recipe ingredients, highlights low / out-of-stock rows, and provides a
// restock procedure (POST /ingredients/:id/adjust-stock).
// RESTAURANT vertical only — MANAGER+ (level 30).

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  PageContainer,
  Button,
  Badge,
  Select,
  Skeleton,
  EmptyState,
  ErrorBoundary,
  DataTable,
  Pagination,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { ingredientService } from '@/services/ingredient.service';
import type { Ingredient, IngredientStats } from '@/services/ingredient.service';
import { deriveIngredientKpis } from './ingredient-stats';
import { RestockModal } from './RestockModal';
import { GLASS, GLASS_FILTER, cn, toneClasses } from '@/constants/design-tokens';
import { formatPaise } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { useDashboardStore } from '@/stores/use-dashboard-store';
import { ApiError } from '@/lib/api-client';

// ─── Stock status ─────────────────────────────────────────────────────────────

type StockLevel = 'out' | 'low' | 'ok';

function stockLevel(i: Ingredient): StockLevel {
  const qty = Number(i.stock_quantity);
  const threshold = Number(i.low_stock_threshold);
  if (qty <= 0) return 'out';
  if (qty <= threshold) return 'low';
  return 'ok';
}

// ─── Icons ──────────────────────────────────────────────────────────────────

function SearchIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function BoxIcon() {
  return (
    <svg className="h-14 w-14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
      <path d="M3.27 6.96L12 12.01l8.73-5.05M12 22.08V12" />
    </svg>
  );
}

// ─── KPI Card ──────────────────────────────────────────────────────────────

function KpiMini({ label, value, tone }: { label: string; value: string | number; tone: 'brand' | 'success' | 'danger' | 'violet' | 'teal' | 'warning' }) {
  const tc = toneClasses(tone);
  return (
    <div className={cn(GLASS, 'relative overflow-hidden p-4')}>
      <div className={`absolute inset-0 bg-gradient-to-br ${tc.accent} pointer-events-none`} />
      <p className="relative text-xs font-medium text-neutral-500 dark:text-neutral-400">{label}</p>
      <p className="relative text-xl font-bold text-neutral-900 dark:text-neutral-100 tabular-nums font-mono mt-1">{value}</p>
    </div>
  );
}

// ─── Constants ─────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

// The table pulls a single wide page and filters/paginates client-side. When a
// tenant has more active ingredients than this cap, the extra rows are not
// fetched — we surface a "showing N of TOTAL" notice (GitHub issue #284) using
// the tenant-wide total from /ingredients/stats so the cap is never silent.
const FETCH_LIMIT = 500;

type StockFilter = 'all' | 'low' | 'out' | 'ok';

const STOCK_OPTIONS = [
  { value: 'all', label: 'All Stock Levels' },
  { value: 'low', label: 'Low & Out of Stock' },
  { value: 'out', label: 'Out of Stock' },
  { value: 'ok', label: 'In Stock' },
];

// ─── Content ────────────────────────────────────────────────────────────────

function IngredientListContent() {
  const { toast } = useToast();
  const currency = useTenantCurrency();

  const [search, setSearch] = useState('');
  const [stockFilter, setStockFilter] = useState<StockFilter>('all');
  const [page, setPage] = useState(1);
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [stats, setStats] = useState<IngredientStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [restockTarget, setRestockTarget] = useState<Ingredient | null>(null);

  const searchRef = useRef<HTMLInputElement>(null);
  // Each location keeps its own stock of an ingredient (docs/master-catalogue-plan.md
  // §17): with a location picked at the top, its rows only, like Menus and Recipes.
  const selectedLocationId = useDashboardStore((s) => s.selectedLocationId);

  const fetchIngredients = useCallback(async () => {
    setIsLoading(true);
    try {
      // Backend pagination is per-page; we pull a wide page and filter client-side
      // for stock-level + search so low-stock highlighting is accurate across the set.
      // KPIs come from the tenant-wide /ingredients/stats aggregate (not this
      // capped slice) so Low/Out-of-Stock never undercount (GitHub issue #284).
      const [result, statsResult] = await Promise.all([
        ingredientService.listIngredients({
          is_active: 'true', limit: FETCH_LIMIT, location_id: selectedLocationId ?? undefined,
        }),
        ingredientService.getStats({ is_active: 'true', location_id: selectedLocationId ?? undefined }),
      ]);
      setIngredients(result.data);
      setStats(statsResult);
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to load ingredients.', variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [toast, selectedLocationId]);

  useEffect(() => { fetchIngredients(); }, [fetchIngredients]);

  // Keyboard: "/" focuses search
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;
      if (e.key === '/') { e.preventDefault(); searchRef.current?.focus(); }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // ─── Derived: filter + paginate ───────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return ingredients.filter((i) => {
      if (q && !i.name.toLowerCase().includes(q) && !(i.category ?? '').toLowerCase().includes(q)) return false;
      if (stockFilter === 'all') return true;
      const lvl = stockLevel(i);
      if (stockFilter === 'low') return lvl === 'low' || lvl === 'out';
      return lvl === stockFilter;
    });
  }, [ingredients, search, stockFilter]);

  const total = filtered.length;
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const pageRows = useMemo(
    () => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filtered, page],
  );

  // Reset to page 1 whenever filters change
  useEffect(() => { setPage(1); }, [search, stockFilter]);

  // ─── KPIs ─────────────────────────────────────────────────────────────────
  // Derived from the tenant-wide /ingredients/stats aggregate (not the ≤500-row
  // fetched slice) so the cards count every active ingredient and never
  // undercount low/out (GitHub issue #284).
  const { totalIngredients, lowStock: lowCount, outOfStock: outCount } = deriveIngredientKpis(stats);

  // True active-ingredient total; the fetched slice is capped at FETCH_LIMIT.
  const totalIngredientCount = stats?.total_ingredients ?? ingredients.length;
  const isCapped = ingredients.length < totalIngredientCount;

  const columns: ColumnDef<Ingredient>[] = useMemo(() => [
    {
      key: 'name', header: 'Ingredient',
      render: (_v: unknown, row: Ingredient) => {
        const lvl = stockLevel(row);
        return (
          <div className="flex items-center gap-2">
            <span
              className={cn(
                'h-2.5 w-2.5 rounded-full shrink-0',
                lvl === 'out' ? 'bg-danger-500 ring-2 ring-danger-200 dark:ring-danger-900' :
                lvl === 'low' ? 'bg-warning-500 ring-2 ring-warning-200 dark:ring-warning-900' :
                'bg-success-500',
              )}
              aria-hidden="true"
            />
            <span className={cn(
              'font-bold',
              lvl === 'out' ? 'text-danger-700 dark:text-danger-300' :
              lvl === 'low' ? 'text-warning-700 dark:text-warning-300' :
              'text-neutral-900 dark:text-neutral-100',
            )}>
              {row.name}
            </span>
          </div>
        );
      },
    },
    {
      key: 'category', header: 'Category',
      render: (_v: unknown, row: Ingredient) => (
        <span className="text-neutral-600 dark:text-neutral-400">{row.category ?? '—'}</span>
      ),
    },
    {
      key: 'stock_quantity', header: 'In Stock', numeric: true,
      render: (_v: unknown, row: Ingredient) => (
        <span className="font-mono tabular-nums">{Number(row.stock_quantity)} {row.unit}</span>
      ),
    },
    {
      key: 'low_stock_threshold', header: 'Reorder At', numeric: true,
      render: (_v: unknown, row: Ingredient) => (
        <span className="font-mono tabular-nums text-neutral-500 dark:text-neutral-400">{Number(row.low_stock_threshold)} {row.unit}</span>
      ),
    },
    {
      key: 'cost', header: 'Unit Cost', numeric: true,
      render: (_v: unknown, row: Ingredient) => <span className="font-mono tabular-nums">{formatPaise(row.cost, currency)}</span>,
    },
    {
      key: 'status', header: 'Status',
      render: (_v: unknown, row: Ingredient) => {
        const lvl = stockLevel(row);
        if (lvl === 'out') return <Badge variant="danger" size="sm">Out of Stock</Badge>;
        if (lvl === 'low') return <Badge variant="warning" size="sm">Low Stock</Badge>;
        return <Badge variant="success" size="sm">In Stock</Badge>;
      },
    },
    {
      key: 'actions', header: '',
      render: (_v: unknown, row: Ingredient) => (
        <Button variant="secondary" size="sm" onClick={() => setRestockTarget(row)}>Restock</Button>
      ),
    },
  ], [currency]);

  return (
    <PageContainer
      title="Ingredients"
      subtitle={`${totalIngredientCount} ingredient${totalIngredientCount !== 1 ? 's' : ''}`}
      breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Ingredients' }]}
    >
      <div className="animate-fade-in">
        {/* KPI Row */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <KpiMini label="Total Ingredients" value={totalIngredients} tone="teal" />
          <KpiMini label="Low Stock" value={lowCount} tone="warning" />
          <KpiMini label="Out of Stock" value={outCount} tone="danger" />
        </div>

        {/* Search + Stock Filter */}
        <div className={cn(GLASS_FILTER, 'p-4 mb-6')}>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 max-w-md">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-neutral-400 dark:text-neutral-500">
                <SearchIcon />
              </div>
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search ingredients... (press / to focus)"
                className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-white/60 dark:bg-neutral-800/60 text-sm text-neutral-900 dark:text-neutral-100 placeholder-neutral-400 dark:placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-teal-500 transition-shadow"
                aria-label="Search ingredients"
              />
            </div>
            <div className="w-full sm:w-56">
              <Select
                options={STOCK_OPTIONS}
                value={stockFilter}
                onChange={(val) => setStockFilter((typeof val === 'string' ? val : val[0] ?? 'all') as StockFilter)}
                placeholder="All Stock Levels"
                aria-label="Filter by stock level"
              />
            </div>
          </div>
        </div>

        {/* Capped-fetch notice — the table only holds the first FETCH_LIMIT rows
            (GitHub issue #284), so tell the user instead of silently dropping the rest. */}
        {!isLoading && isCapped && (
          <div className="mb-4 rounded-xl border border-warning-200 dark:border-warning-900 bg-warning-50/70 dark:bg-warning-950/40 px-4 py-3 text-sm text-warning-800 dark:text-warning-200">
            Showing the first {ingredients.length} of {totalIngredientCount} ingredients. Narrow your search or category to reach the rest; the KPI counts above reflect all {totalIngredientCount}.
          </div>
        )}

        {/* Table */}
        {isLoading ? (
          <div className={cn(GLASS, 'p-6 space-y-4')}>
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="flex gap-4">
                <Skeleton variant="text" width="25%" height={18} />
                <Skeleton variant="text" width="15%" height={18} />
                <Skeleton variant="text" width="12%" height={18} />
                <Skeleton variant="text" width="12%" height={18} />
                <Skeleton variant="text" width="12%" height={18} />
                <Skeleton variant="rect" width={80} height={24} />
              </div>
            ))}
          </div>
        ) : pageRows.length === 0 ? (
          <EmptyState
            variant="page"
            icon={<BoxIcon />}
            title={ingredients.length === 0 ? 'No ingredients yet' : 'No matching ingredients'}
            description={ingredients.length === 0 ? 'Ingredients are created from the recipe module' : 'Try adjusting your search or stock filter'}
          />
        ) : (
          <>
            <div className={cn(GLASS, 'overflow-hidden')}>
              <DataTable<Ingredient> columns={columns} data={pageRows} />
            </div>
            {totalPages > 1 && (
              <div className="mt-4">
                <Pagination currentPage={page} totalPages={totalPages} totalItems={total} pageSize={PAGE_SIZE} onPageChange={setPage} />
              </div>
            )}
          </>
        )}
      </div>

      {restockTarget && (
        <RestockModal
          ingredient={restockTarget}
          onClose={() => setRestockTarget(null)}
          onSuccess={fetchIngredients}
        />
      )}
    </PageContainer>
  );
}

export function IngredientListPage() {
  return <ErrorBoundary><IngredientListContent /></ErrorBoundary>;
}
